import { dateFormatterWithLeadingZero, nFormatter } from '@src/utility/commonUtilityServices';
import {
  all,
  call,
  cancelled,
  debounce,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import {
  CASE_SUBMISSION_DETAILS_CANCEL,
  CASE_SUBMISSIONS_OVER_TIME_DATA_CANCEL,
  // CASE_SUBMISSION_CHANNELS_DATA_CANCEL,
  CASE_SUBMISSIONS_OVER_TIME_DATA_REQUEST,
  PRIORAUTH_ALL_PROVIDERS_QUICKFILTER_DATA_REQUEST,
  PRIORAUTH_CPT_APPROVALS_REQUEST,
  PRIORAUTH_CPT_CODES_REQUEST,
  PRIORAUTH_EXTENDED_TINS_TOP_FILTER_DATA_REQUEST,
  PRIORAUTH_FILTERS_CHANGED,
  PRIORAUTH_METRIC_DEFINITIONS_DATA_REQUEST,
  PRIORAUTH_NOT_APPROVED_REASONS_REQUEST,
  PRIORAUTH_TAX_ID_QUICKFILTER_DATA_REQUEST,
  PRIORAUTH_TURNAROUND_TIME_CANCEL
} from '../actions/actionTypes';
import {
  caseSubmissionChannelsLoading,
  caseSubmissionDetailsLoading,
  caseSubmissionsOverTimeLoading,
  fetchCPTApprovalsFailure,
  fetchCPTApprovalsSuccess,
  fetchPACPTCodesFailure,
  fetchPACPTCodesSuccess,
  fetchPANotApprovedReasonsFailure,
  fetchPANotApprovedReasonsSuccess,
  getCaseSubmissionChannelsDataError,
  getCaseSubmissionChannelsDataSuccess,
  getCaseSubmissionDetailsError,
  getCaseSubmissionDetailsSuccess,
  getCaseSubmissionsOverTimeDataError,
  getCaseSubmissionsOverTimeDataSuccess,
  getInsightCardDataError,
  getInsightCardDataSuccess,
  getPriorAuthAllProvidersQuickFilterDataError,
  getPriorAuthAllProvidersQuickFilterDataSuccess,
  getPriorAuthExtendedTinsTopFilterDataError,
  getPriorAuthExtendedTinsTopFilterDataSuccess,
  getPriorAuthMetricDefinitionsDataError,
  getPriorAuthMetricDefinitionsDataSuccess,
  getPriorAuthTaxIdQuickFilterDataError,
  getPriorAuthTaxIdQuickFilterDataSuccess,
  getPriorAuthTurnaroundTimeError,
  getPriorAuthTurnaroundTimeSuccess,
  insightCardLoading,
  priorAuthPollingCompleted,
  priorAuthPollingFailed,
  priorAuthPollingReset,
  priorAuthPollingStarted,
  priorAuthTurnaroundTimeLoading,
  setCPTApprovalsLoading,
  setPANotApprovedLoading,
  setPriorAuthBasePayload
} from '../actions/PriorAuth/priorAuthActions';
import { isLoading } from '../actions/SiteOfServiceAction';
import {
  fetchCPTApprovalsData,
  fetchCPTApprovalsDataBulk,
  fetchPACPTCodes,
  fetchPACPTCodesBulk,
  fetchPANotApprovedReasons,
  fetchPANotApprovedReasonsBulk,
  getCaseSubmissionChannelsData,
  getCaseSubmissionChannelsDataBulk,
  getCaseSubmissionRawData,
  getCaseSubmissionRawDataBulk,
  getCaseSubmissionsOverTimeData,
  getInsightCardData,
  getInsightCardDataBulk,
  getMetricsData,
  getPriorAuthQuickFiltersData,
  getPriorAuthTurnaroundTimeData,
  getPriorAuthTurnaroundTimeDataBulk,
  getQuickFiltersData,
} from '../services/priorAuth/priorAuth.service';
import { checkIsInternal, checkIsSimulationMode, checkContractIdFlow } from '@src/utility/createStorage';
import { getEffectiveContractIdValuesFromSession } from '@src/CommonComponents/ContractIdFilter/contractIdFilter';
import { PROVIDER_SETTING_OPTIONS } from '@src/apps/PriorAuth/constant';
import { LOB_OPTIONS } from '@src/apps/PriorAuth/GlobalFilters/constants/constants';
import { ROUTES_PATHS } from '@src/constants/routes';
const SESSION_STATE_KEY = 'state';

const getLineOfBusinessCodes = (value: unknown): string[] => {
  const values = Array.isArray(value) ? value : value == null ? [] : [value];

  return values
    .map(item => {
      const option = item && typeof item === 'object'
        ? item as { value?: unknown; Value?: unknown; Name?: unknown }
        : undefined;
      const rawValue = option?.value ?? option?.Value ?? option?.Name ?? item;
      const normalizedValue = String(rawValue ?? '').trim();
      return LOB_OPTIONS.find(filterOption =>
        filterOption.value === normalizedValue || filterOption.label === normalizedValue
      );
    })
    .filter((option): option is (typeof LOB_OPTIONS)[number] =>
      Boolean(option) && option.value !== 'All' && option.value !== 'UN' && !option.disabled
    )
    .map(option => option.value);
};

export function* fetchPriorAuthTaxIdQuickFilterData(action: any) {
  try {
    const payload = action?.payload || {};
    yield put(isLoading(true));
    const response = yield call(getPriorAuthQuickFiltersData, payload);
    yield put(getPriorAuthTaxIdQuickFilterDataSuccess(response?.Data));
  } catch (error: any) {
    yield put(
      getPriorAuthTaxIdQuickFilterDataError({
        message:
          error?.message ||
          'Unknown error occurred while fetching prior auth quick filters.',
        stack: error?.stack || '',
        source: 'fetchPriorAuthTaxIdQuickFilterData',
      })
    );
    console.error('Error fetching quick filters:', error);
  } finally {
    yield put(isLoading(false));
  }
}

export function* fetchPriorAuthAllProvidersQuickFilterData(action: any) {
  try {
    const payload = action?.payload || {};
    yield put(isLoading(true));
    const response = yield call(getPriorAuthQuickFiltersData, payload);
    yield put(getPriorAuthAllProvidersQuickFilterDataSuccess(response?.Data));
  } catch (error: any) {
    yield put(
      getPriorAuthAllProvidersQuickFilterDataError({
        message:
          error?.message ||
          'Unknown error occurred while fetching prior auth quick filters.',
        stack: error?.stack || '',
        source: 'fetchPriorAuthAllProvidersQuickFilterData',
      })
    );
    console.error('Error fetching quick filters:', error);
  } finally {
    yield put(isLoading(false));
  }
}

export function* fetchPriorAuthExtendedTinsTopFilterData(action: any) {
  try {
    const payload = action?.payload || {};
    yield put(isLoading(true));
    const response = yield call(getQuickFiltersData, payload);
    yield put(getPriorAuthExtendedTinsTopFilterDataSuccess(response?.Data));
  } catch (error: any) {
    yield put(
      getPriorAuthExtendedTinsTopFilterDataError({
        message:
          error?.message ||
          'Unknown error occurred while fetching prior auth quick filters.',
        stack: error?.stack || '',
        source: 'fetchPriorAuthExtendedTinsTopFilterData',
      })
    );
    console.error('Error fetching quick filters:', error);
  } finally {
    yield put(isLoading(false));
  }
}

export function* fetchPriorAuthMetricDefinitionsData() {
  try {
    yield put(isLoading(true));
    const response = yield call(() => getMetricsData());
    yield put(getPriorAuthMetricDefinitionsDataSuccess(response));
  } catch (error: any) {
    yield put(
      getPriorAuthMetricDefinitionsDataError({
        message:
          error?.message ||
          'Unknown error occurred while fetching prior auth metric defintions.',
        stack: error?.stack || '',
        source: 'fetchPriorAuthMetricDefinitionsData',
      })
    );
    console.error('Error fetching metric definitions:', error);
  } finally {
    yield put(isLoading(false));
  }
}

export function* fetchCaseSubmissionDetailsData(action: any) {
  const { payload, key } = action;
  let stopPollingFn: (() => void) | undefined;
  try {
    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }
    yield put(caseSubmissionDetailsLoading(true));
    const { result, cancel } = yield race({
      result: call(
        !key ? getCaseSubmissionRawData : getCaseSubmissionRawDataBulk,
        payload,
        {
          onStop: (stopFn: () => void) => {
            stopPollingFn = stopFn;
          },
        }
      ),
      cancel: take(CASE_SUBMISSION_DETAILS_CANCEL),
    });
    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(caseSubmissionDetailsLoading(false));
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
      }
      return;
    }
    yield put(getCaseSubmissionDetailsSuccess(result));
    yield put(caseSubmissionDetailsLoading(false));
    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(
      getCaseSubmissionDetailsError(error?.message || 'An error occurred')
    );
    yield put(caseSubmissionDetailsLoading(false));
    if (key) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(caseSubmissionDetailsLoading(false));
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
  }
}

export function* fetchPriorAuthTurnaroundTimeData(action: any) {
  const { payload, key } = action;
  let stopPollingFn: (() => void) | undefined;
  try {
    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }
    yield put(priorAuthTurnaroundTimeLoading(true));
    const { result, cancel } = yield race({
      result: call(
        !key
          ? getPriorAuthTurnaroundTimeData
          : getPriorAuthTurnaroundTimeDataBulk,
        payload,
        {
          onStop: (stopFn: () => void) => {
            stopPollingFn = stopFn;
          },
        }
      ),
      cancel: take(PRIORAUTH_TURNAROUND_TIME_CANCEL),
    });
    if (cancel) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(priorAuthTurnaroundTimeLoading(false));
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement polling counter on cancel (bulk flow only)
      }
      return;
    }
    yield put(getPriorAuthTurnaroundTimeSuccess(result));
    yield put(priorAuthTurnaroundTimeLoading(false));
    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(
      getPriorAuthTurnaroundTimeError({
        message:
          error?.message ||
          'Unknown error occurred while fetching turnaround time.',
        stack: error?.stack || '',
        source: 'fetchPriorAuthTurnaroundTimeData',
      })
    );
    yield put(priorAuthTurnaroundTimeLoading(false));
    if (key) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      if (typeof stopPollingFn === 'function') {
        stopPollingFn();
      }
      yield put(priorAuthTurnaroundTimeLoading(false));
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
  }
}

export function* fetchInsightCardData(action: any) {
  try {
    const { payload, key } = action || {};

    // Always enforce TimeFilter to be Last12Months for case-submission-by-channel API
    const updatedPayload = {
      ...payload,
      TimeFilter: 'Last12Months'
    };

    const finalPayloadKeys = ['ProviderSysKey', 'ProviderType', 'TimeFilter', 'TimeFilterText', 'Region', 'State', 'Tins', 'Npi', 'Market', 'ContractIds', 'Specialty'];

    // Option 1: Using reduce to create a filtered object
    const finalPayload = Object.keys(updatedPayload).reduce((acc, key) => {
      if (finalPayloadKeys.includes(key)) {
        acc[key] = updatedPayload[key];
      }
      return acc;
    }, {});


    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }
    yield put(insightCardLoading(true));
    const response = yield call(
      !key ? getInsightCardData : getInsightCardDataBulk,
      finalPayload
    );
    yield put(getInsightCardDataSuccess(response));
    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(
      getInsightCardDataError({
        message:
          error?.message ||
          'Unknown error occurred while fetching insight card data.',
        stack: error?.stack || '',
        source: 'fetchInsightCardData',
      })
    );
    console.error('Error fetching insight card data:', error);
    const { key } = action || {};
    if (key) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      const { key } = action || {};
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
    yield put(insightCardLoading(false));
  }
}

export function* fetchCaseSubmissionChannelsData(action: any) {
  try {
    const { payload, key } = action || {};

    yield put(caseSubmissionChannelsLoading(true));
    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }
    const response = yield call(!key ? getCaseSubmissionChannelsData : getCaseSubmissionChannelsDataBulk, payload);

    if (response?.Result !== null && Object?.keys(response)?.length > 0) {

      const result = [
        {
          title: 'Others',
          value: ((response?.ChannelDetails?.Other / response?.ChannelDetails?.Total) * 100).toFixed(
            1
          ),
          numeric: response?.ChannelDetails?.Other,
          maxValue: response?.ChannelDetails?.Total,
          display: {
            num: nFormatter(response?.ChannelDetails?.Other),
            denom: nFormatter(response?.ChannelDetails?.Total),
          },
          id: 'Others',
          timePeriod:
            dateFormatterWithLeadingZero(response.PeriodStart) +
            '&ndash;' +
            dateFormatterWithLeadingZero(response.PeriodEnd),
        },
        {
          title: 'PAAN',
          value: ((response?.ChannelDetails?.Paan / response?.ChannelDetails?.Total) * 100).toFixed(
            1
          ),
          numeric: response?.ChannelDetails?.Paan,
          maxValue: response?.ChannelDetails?.Total,
          display: {
            num: nFormatter(response?.ChannelDetails?.Paan),
            denom: nFormatter(response?.ChannelDetails?.Total),
          },
          id: 'Paan',
        },
        {
          title: 'Phone',
          value: ((response?.ChannelDetails?.Phone / response?.ChannelDetails?.Total) * 100).toFixed(
            1
          ),
          numeric: response?.ChannelDetails?.Phone,
          maxValue: response?.ChannelDetails?.Total,
          display: {
            num: nFormatter(response?.ChannelDetails?.Phone),
            denom: nFormatter(response?.ChannelDetails?.Total),
          },
          id: 'Phone',
        },
        {
          title: 'EDI',
          value: ((response?.ChannelDetails?.Edi / response?.ChannelDetails?.Total) * 100).toFixed(1),
          numeric: response?.ChannelDetails?.Edi,
          display: {
            num: nFormatter(response?.ChannelDetails?.Edi),
            denom: nFormatter(response?.ChannelDetails?.Total),
          },
          maxValue: response?.ChannelDetails?.Total,
          id: 'EDI',
        },
        {
          title: 'Fax',
          value: ((response?.ChannelDetails?.Fax / response?.ChannelDetails?.Total) * 100).toFixed(1),
          numeric: response?.ChannelDetails?.Fax,
          maxValue: response?.ChannelDetails?.Total,
          display: {
            num: nFormatter(response?.ChannelDetails?.Fax),
            denom: nFormatter(response?.ChannelDetails?.Total),
          },
          id: 'Fax',
        },
        {
          title: 'Rhyme',
          value: (
            (response?.ChannelDetails?.Rhyme / response?.ChannelDetails?.Total) *
            100
          ).toFixed(1),
          numeric: response?.ChannelDetails?.Rhyme,
          maxValue: response?.ChannelDetails?.Total,
          display: {
            num: nFormatter(response?.ChannelDetails?.Rhyme),
            denom: nFormatter(response?.ChannelDetails?.Total),
          },
          id: 'Rhyme',
        },
      ];

      if (result && result.length > 0) {
        result.sort((a, b) => parseFloat(b.numeric) - parseFloat(a.numeric));
      }

      yield put(getCaseSubmissionChannelsDataSuccess(result));
    } else {
      yield put(getCaseSubmissionChannelsDataSuccess([]));
    }

    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(
      getCaseSubmissionChannelsDataError({
        message:
          error?.message ||
          'Unknown error occurred while fetching case submission channels data.',
        stack: error?.stack || '',
        source: 'fetchCaseSubmissionChannelsData',
      })
    );
    console.error('Error fetching case submission channels data:', error);
    const { key: keyFromAction } = action || {};
    if (keyFromAction) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled()) {
      const { key } = action || {};
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
    yield put(caseSubmissionChannelsLoading(false));
  }
}

export function* fetchCaseSubmissionsOverTimeData(action: any) {
  let stopPolling: (() => void) | undefined;
  try {
    const { payload = {}, key } = action || {};
    yield put(caseSubmissionsOverTimeLoading(true));

    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }

    let response;
    if (key) {
      // Use bulk processing when key flag is set (high volume TIN flow)
      const { result, cancel } = yield race({
        result: call(getCaseSubmissionsOverTimeData, payload, key, {
          onStop: (stopFn: () => void) => {
            stopPolling = stopFn;
          },
        }),
        cancel: take(CASE_SUBMISSIONS_OVER_TIME_DATA_CANCEL),
      });

      if (cancel) {
        if (stopPolling) {
          stopPolling();
        }
        yield put(caseSubmissionsOverTimeLoading(false));
        yield put(priorAuthPollingCompleted()); // Decrement polling counter on cancel
        return;
      }

      response = result;
    } else {
      // Use standard processing
      response = yield call(getCaseSubmissionsOverTimeData, payload, key);
    }

    yield put(getCaseSubmissionsOverTimeDataSuccess(response));

    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(
      getCaseSubmissionsOverTimeDataError(
        error?.message || 'Unknown error occurred while fetching case submissions over time data.'
      )
    );
    console.error('Saga - Error fetching case submissions over time data:', error);
    const { key } = action || {};
    if (key) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    if (yield cancelled() && stopPolling) {
      // stopPolling();
      const { key } = action || {};
      if (key) {
        yield put(priorAuthPollingCompleted()); // Decrement on cancellation (bulk flow only)
      }
    }
    yield put(caseSubmissionsOverTimeLoading(false));
  }
}

// NEW: Fetch all PriorAuth data when filters change
export function* fetchAllPriorAuthData(action: any) {
  try {
    const currentPath = window.location.pathname;
    const isPriorAuth = currentPath.includes(ROUTES_PATHS.PriorAuth.Summary);
    const isOnPANotApprovedPage = currentPath.includes(ROUTES_PATHS.PriorAuth.PANotApproved);
    const isOnCPTApprovalsPage = currentPath.includes(ROUTES_PATHS.PriorAuth.PACptCodes);
    if (!isPriorAuth) {
      return;
    }

    // Reset polling counter at the start of new batch to clear any orphaned increments
    yield put(priorAuthPollingReset());
    const reduxState = yield select((state: any) => state?.priorAuthReducer?.currentFilters);
    const filters = {
      ...reduxState,
      ...action.payload
    };

    // Check if we're in geo flow or TIN/NPI flow
    const geoCriteria = sessionStorage.getItem('GeoCriteria');
    const isGeoFlow = geoCriteria && geoCriteria !== 'null';
    const highVolumeTinFlag = sessionStorage.getItem('HIVOLUMETINFLOW') === 'true'
      || sessionStorage.getItem('hiVolumeContractFlow') === 'true';

    const getTimeFilterValues = (timePeriodValue: string) => {
      const standardPeriods = ['Last30Days', 'Last3Months', 'Last6Months', 'Last12Months', 'YTD'];
      const isStandardPeriod = standardPeriods.includes(timePeriodValue);

      return {
        TimeFilter: isStandardPeriod ? timePeriodValue : 'CalendarYear',
        TimeFilterText: filters?.timeperiod?.Name
      };
    };

    const timePeriodValue = filters?.timeperiod?.value || filters?.timeperiod?.Name || 'Last12Months';
    const timeFilterValues = getTimeFilterValues(timePeriodValue);

    // Build base payload from filters
    const basePayload: any = {
      TimeFilter: timeFilterValues.TimeFilter,
      TimeFilterText: timeFilterValues.TimeFilterText,
      ProviderSysKey: (checkIsInternal() && !checkIsSimulationMode()) ? filters?.ProviderSysKey : undefined,
      ProviderType: "RF"
    };

    try {
      const storedState = sessionStorage.getItem('state');
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        const priorAuthFilters = parsedState.priorAuthFilters || {};

        const hasValue = (value: any) => {
          if (value === null || value === undefined) return false;
          if (typeof value === 'string') {
            const trimmedValue = value.trim();
            if (trimmedValue === '' || trimmedValue.toLowerCase() === 'all') return false;
          }
          return true;
        };

        if (hasValue(priorAuthFilters.channel)) {
          basePayload.Channel = priorAuthFilters.channel;
        }

        const lobCodes = getLineOfBusinessCodes(priorAuthFilters.lineOfBusiness);
        if (lobCodes.length > 0) {
          basePayload.Lob = lobCodes.join(',');
        }

        if (priorAuthFilters.providerSetting) {
          const providerVal = priorAuthFilters.providerSetting.trim();
          if (providerVal.toLowerCase() === 'all' || providerVal === '') {
            // "All" selected — omit ProviderType so backend returns distinct cases across all roles
            delete basePayload.ProviderType;
          } else {
            const matchingProvider = PROVIDER_SETTING_OPTIONS.find(
              providerSetting => providerSetting.Name === providerVal || providerSetting.value === providerVal
            );
            basePayload.ProviderType = matchingProvider ? matchingProvider.value : providerVal;
          }
        }

        if (hasValue(priorAuthFilters.serviceCategory)) {
          basePayload.ServiceCategory = priorAuthFilters.serviceCategory;
        }

        if (hasValue(priorAuthFilters.serviceSetting)) {
          basePayload.ServiceType = priorAuthFilters.serviceSetting.toUpperCase().replace(/ /g, '_');
        }

        const specialtyRaw = priorAuthFilters.specialty;
        const specialtyArr = Array.isArray(specialtyRaw)
          ? specialtyRaw
          : specialtyRaw ? [specialtyRaw] : [];
        const specialtyCsv = specialtyArr
          .map((s: any) => s.Name || s.Value || s.title)
          .filter((v: string) => hasValue(v))
          .join(',');
        if (specialtyCsv) {
          basePayload.Specialty = specialtyCsv;
        }

        if (hasValue(priorAuthFilters.groups)) {
          basePayload.Groups = priorAuthFilters.groups;
        }
      }
    } catch (error) {
      console.error('Failed to merge sessionStorage filters:', error);
    }

    // Handle TIN/NPI flow filters
    const isContractIdFlow = checkContractIdFlow();

    if (isContractIdFlow) {
      const selectedContractIds = getEffectiveContractIdValuesFromSession();
      if (selectedContractIds.length > 0) {
        basePayload.ContractIds = selectedContractIds;
      }
      delete basePayload.ProviderSysKey;
      delete basePayload.ProviderType;
    } else {
    // Handle TIN/NPI flow filters
    if (!isGeoFlow) {
      // Read parsedState first before using it
      const storedStateForTins = sessionStorage.getItem('state');
      const parsedStateForTins = storedStateForTins ? JSON.parse(storedStateForTins) : {};

      const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
      const multiSelectedTins = sessionStorage.getItem('multiSelectedTins');
      const isMultiTinFlow = multiTinNavigation === 'true';

      const sessionState = JSON.parse(sessionStorage.getItem(SESSION_STATE_KEY) || '{}')?.extendedCorporateTinSelected;
      const selectedExtendedTin = sessionState?.Tin ? sessionState?.Tin : '';

      // Build Tins string from all available sources with priority
      let tinsToInclude = '';

      // Priority 0: Extended corporate TIN cross-journey selection
      if (selectedExtendedTin && selectedExtendedTin !== "All Selected TINs") {
        tinsToInclude = selectedExtendedTin;
      }
      // Priority 1: Multi-TIN flow with multiNpiFlag
      else if (isMultiTinFlow && multiSelectedTins && multiSelectedTins !== 'All' && basePayload?.multiNpiFlag) {
        tinsToInclude = multiSelectedTins;
      }
      // Priority 2: Redux filter tin array (most recent user selection from top filter/chips)
      else if (filters?.tin && Array.isArray(filters.tin) && filters.tin.length > 0) {
        const filteredTins = filters.tin.filter((t: any) => {
          const tinValue = t.value || t.displayName || t;
          return tinValue && tinValue !== 'All' && tinValue !== 'All Tax IDs' && tinValue !== 'ALL';
        });
        if (filteredTins.length > 0) {
          tinsToInclude = filteredTins.map((t: any) => t.value || t.displayName).join(',');
        }
      }
      // Priority 3: sessionStorage priorAuthFilters.taxId (global filter / cross-journey)
      else if (parsedStateForTins?.priorAuthFilters?.taxId && Array.isArray(parsedStateForTins.priorAuthFilters.taxId)) {
        const filteredTins = parsedStateForTins.priorAuthFilters.taxId.filter((t: any) =>
          t.Tin && t.Tin !== 'All' && t.Tin !== 'ALL'
        );
        if (filteredTins.length > 0) {
          tinsToInclude = filteredTins.map((t: any) => t.Tin).join(',');
        }
      }
      // Priority 4: Legacy string tin from Redux
      else if (filters?.tin && typeof filters.tin === 'string' && filters.tin.trim() !== '') {
        const tinsValue = filters.tin.split(",").filter((val) => !['all', 'All', 'ALL']?.includes(val)).toString();
        if (tinsValue) {
          tinsToInclude = tinsValue;
        }
      }

      // Always include Tins when available (even when NPI is selected - production requirement)
      if (tinsToInclude) {
        basePayload.Tins = tinsToInclude;
      }

      // Add Npis if selected (Tins stays in payload alongside Npis)
      // Support both topFilterNpiMultiSelect (multi-select) and legacy filters.allProviders (single-select)
      try {
        const state = JSON.parse(sessionStorage.getItem('state') || '{}');
        const topNpis = state?.topFilterNpiMultiSelect;

        if (Array.isArray(topNpis) && topNpis.length > 0) {
          // Multi-select format: extract Npi values and filter out sentinel values.
          const npiValues = topNpis
            .map((n: any) => n.Npi)
            .filter((npi: string) => {
              if (!npi) return false;
              const normalized = String(npi).trim().toLowerCase();
              return normalized !== 'all' && normalized !== 'all providers';
            })
            .join(',');
          if (npiValues) {
            basePayload.Npi = npiValues;
          }
        } else if (filters?.allProviders) {
          // Fallback to legacy single-select format for backward compatibility
          const isAllProviders = filters.allProviders.id === 'ALL' || filters.allProviders.value === 'All Providers' || filters.allProviders.Name === 'All Providers' || filters.allProviders.displayName === 'All Providers';
          if (!isAllProviders && Array.isArray(filters.allProviders)) {
            // Multi-select array from filters
            const npiValues = filters.allProviders
              .map((p: any) => p.value || p.displayName)
              .filter((v: string) => v && v !== 'All' && v !== 'All Providers')
              .join(',');
            if (npiValues) {
              basePayload.Npi = npiValues;
            }
          } else if (!isAllProviders && typeof filters.allProviders === 'object') {
            // Single object fallback
            basePayload.Npi = filters.allProviders.value || filters.allProviders.displayName;
          }
        }
      } catch (e) {
        console.warn('Failed to read multi-NPI value from sessionStorage:', e);
      }

      if (basePayload.Npi && !basePayload.Tins) {
        const npiScopedTins = (parsedStateForTins?.taxIdOptionsList || [])
          .map((t: any) => t?.Tin)
          .filter((t: any) => t && t !== 'All' && t !== 'ALL');
        if (npiScopedTins.length > 0) {
          basePayload.Tins = npiScopedTins.join(',');
        }
      }
    }



    // Handle Geo flow filters
    if (isGeoFlow) {
      // The global filter is multi-select; an explicit empty selection clears LOB.
      if (filters?.lob !== undefined) {
        const geoLobCodes = getLineOfBusinessCodes(filters.lob);
        if (geoLobCodes.length > 0) {
          basePayload.Lob = geoLobCodes.join(',');
        } else {
          delete basePayload.Lob;
        }
      }

      // Add Provider Type if selected (only if not "All")
      if (filters?.requestProvider) {
        const providerTypeValue = filters.requestProvider.value || filters.requestProvider.Name;
        if (providerTypeValue && providerTypeValue !== 'All') {
          basePayload.ProviderType = providerTypeValue;
        } else {
          // "All" selected — omit ProviderType so backend returns distinct cases across all roles
          delete basePayload.ProviderType;
        }
      }
      // If no requestProvider filter set, keep default "RF" from basePayload

      // Add Region if selected
      if (filters?.region) {
        const regionValue = filters.region.value || filters.region.Name;
        basePayload.Region = regionValue;
      }

      // Add State(s) - always include (empty string if not selected)
      if (filters?.state) {
        if (Array.isArray(filters.state) && filters.state.length > 0) {
          const stateValue = filters.state.map((s: any) => s.value || s.Name).join(',');
          basePayload.State = stateValue;
        } else if (filters.state.value || filters.state.Name) {
          basePayload.State = filters.state.value || filters.state.Name;
        } else {
          basePayload.State = '';
        }
      } else {
        basePayload.State = '';
      }

      // Add Market(s) - always include Market field (empty array if not selected)
      if (filters?.market) {
        if (Array.isArray(filters.market) && filters.market.length > 0) {
          // Market can be array of market numbers or objects
          basePayload.Market = filters.market.map((m: any) => m.value || m.Name);
        } else if (filters.market.value || filters.market.Name) {
          basePayload.Market = [filters.market.value || filters.market.Name];
        } else {
          // Empty array if market exists but is empty
          basePayload.Market = [];
        }
      } else {
        basePayload.Market = [];
      }
    }

    } // end else (!isContractIdFlow)

    // Check if multi-TIN flow has no TINs available (getPATopFilter returned empty array)
    const multiTinNavigation = sessionStorage.getItem('multiTinNavigation');
    const isMultiTinFlow = multiTinNavigation === 'true';
    const hasNoTins = !isContractIdFlow && !isGeoFlow && isMultiTinFlow && !basePayload.Tins;

    if (hasNoTins && !highVolumeTinFlag) {
      return; // Exit early - no API calls
    }

    // Store base payload in Redux for tab changes in CaseSubmissionsOverTimeCard
    yield put(setPriorAuthBasePayload(basePayload));

    // Read parsedState AFTER building basePayload
    const storedState = sessionStorage.getItem('state');
    const parsedState = storedState ? JSON.parse(storedState) : {};

    const paNotApprovedPayload: any = {
      ...basePayload, // Include all base payload fields (Tins, Npi, ProviderSysKey, Region, etc.)
      TimeFilter : getTimeFilterValues(parsedState?.priorAuthFilters?.timePeriod).TimeFilter,
      TimeFilterText : parsedState?.priorAuthFilters?.timePeriodText,
      Limit: 10,
      CaseStatus: 'NOTAPPROVED',
      key: highVolumeTinFlag // Add key for bulk flow tracking
    };

    const cptApprovalsPayload: any = {
      ...basePayload, // Include all base payload fields (Tins, Npi, ProviderSysKey, Region, etc.)
      TimeFilter : getTimeFilterValues(parsedState?.priorAuthFilters?.timePeriod).TimeFilter,
      TimeFilterText : parsedState?.priorAuthFilters?.timePeriodText,
      Count: 10,
      CaseStatus: 'APPROVED',
      key: highVolumeTinFlag // Add key for bulk flow tracking
    };

    // Special payload for case submissions over time when LOB is "All" and RequestType is LOB
    const caseSubmissionsOverTimePayload = {
      ...basePayload,
      RequestType: 'LOB', // Default to LOB view when filters change
      key: highVolumeTinFlag // Add key for bulk flow
    };
    if (isGeoFlow && filters?.lob !== undefined && getLineOfBusinessCodes(filters.lob).length === 0 && caseSubmissionsOverTimePayload.RequestType === 'LOB') {
      // For "All" LOB with LOB request type, send all LOB codes for case submissions over time only
      caseSubmissionsOverTimePayload.Lob = 'EI,MR,CS,EXC';
    }

    if (highVolumeTinFlag) {
      basePayload.Tins = undefined;
      caseSubmissionsOverTimePayload.Tins = undefined;
      cptApprovalsPayload.Tins = undefined;
      paNotApprovedPayload.Tins = undefined;
    }

    // Dispatch all data fetch actions in parallel
    // Build the array of API calls based on the current page
    const apiCalls: any[] = [];

    // Only call PA Not Approved API if on PA Not Approved page
    // Note: PA Not Approved API internally calls CPT codes API for each reason
    if (isOnPANotApprovedPage) {
      apiCalls.push(call(fetchPANotApprovedReasonsSaga, { payload: paNotApprovedPayload, key: highVolumeTinFlag }));
    }
    // Only call CPT Approvals API if on CPT Approvals page
    else if (isOnCPTApprovalsPage) {
      apiCalls.push(call(fetchCPTApprovalsSaga, { payload: cptApprovalsPayload, key: highVolumeTinFlag }));
    }
    // If on main PriorAuth summary page (not on specific detail pages), call common dashboard APIs
    else if (isPriorAuth) {
      apiCalls.push(
        call(fetchCaseSubmissionDetailsData, { payload: basePayload, key: highVolumeTinFlag }),
        call(fetchInsightCardData, { payload: basePayload, key: highVolumeTinFlag }),
        call(fetchCaseSubmissionChannelsData, { payload: basePayload, key: highVolumeTinFlag }),
        call(fetchCaseSubmissionsOverTimeData, { payload: caseSubmissionsOverTimePayload, key: caseSubmissionsOverTimePayload.key }),
        call(fetchPriorAuthTurnaroundTimeData, { payload: basePayload, key: highVolumeTinFlag })
      );
    }
    if (apiCalls.length > 0) {
      yield all(apiCalls);
    }
  } catch (error: any) {
    console.error('Error in fetchAllPriorAuthData saga:', error);
  }
}

function transformNotApprovedData(apiResponse: any) {
  if (!apiResponse || !apiResponse.Data || !apiResponse.Data.Details || apiResponse.Data.Details.length === 0) {
    return [];
  }

  const details = apiResponse.Data.Details;
  const maxValue = Math.max(...details.map(item => item.Count));

  return details.map((item, index) => ({
    title: item.Reason,
    data: {
      barText: item.Reason,
      barData: item.Count,
      barColor: '#3381ff'
    },
    top5: [],
    titleId: index + 1,
    sortData: `${index}`,
    timeperiod: `${dateFormatterWithLeadingZero(apiResponse.Data.PeriodStart)} - ${dateFormatterWithLeadingZero(apiResponse.Data.PeriodEnd)}`
  }));
}

function* fetchPANotApprovedReasonsSaga(action: any): Generator<any, void, any> {
  const { payload, key } = action;
  const isContractIdFlow = checkContractIdFlow();
  try {
    yield put(setPANotApprovedLoading(true));

    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }

    // Use bulk API if key is truthy, otherwise use regular API
    const response: any = yield call(!key ? fetchPANotApprovedReasons : fetchPANotApprovedReasonsBulk, payload);

    const transformedData = transformNotApprovedData(response);
    const totalCount = response.Data?.Details?.reduce((sum, item) => sum + item.Count, 0) || 0;

    // Dispatch success FIRST with empty top5 - CPT codes will populate it later
    yield put(fetchPANotApprovedReasonsSuccess({
      data: transformedData,
      totalCount: totalCount,
      periodStart: response.Data?.PeriodStart || null,
      periodEnd: response.Data?.PeriodEnd || null
    }));

    if (response.Data?.Details && response.Data.Details.length > 0) {
      const cptCodeRequests = response.Data.Details.map((detail: any) => {
        let cptPayload: any;
        if (key) {
          cptPayload = isContractIdFlow
            ? {
              TimeFilter: payload.TimeFilter || 'Last12Months',
              TimeFilterText: payload.TimeFilterText || 'Last 12 Months',
              ContractIds: payload.ContractIds || getEffectiveContractIdValuesFromSession(),
              CaseStatus: 'NOTAPPROVED',
              ReasonDesc: detail.Reason,
              Count: 5,
              Specialty: payload.Specialty || '',
            }
            : {
              TimeFilter: payload.TimeFilter || 'Last12Months',
              TimeFilterText: payload.TimeFilterText || 'Last 12 Months',
              ProviderSysKey: (checkIsInternal() && !checkIsSimulationMode()) ? payload?.ProviderSysKey : undefined,
              TinGroupId: payload.TinGroupId || sessionStorage.getItem('tinGroupId') || '',
              CaseStatus: 'NOTAPPROVED',
              ReasonDesc: detail.Reason,
              Count: 5,
              Specialty: payload.Specialty || '',
            };
        } else {
          cptPayload = {
            TimeFilter: payload.TimeFilter,
            TimeFilterText: payload.TimeFilterText || '',
            CaseStatus: payload.CaseStatus,
            ReasonDesc: detail.Reason,
            Count: 5,
            Specialty: payload.Specialty || '',
          };
        }

        // Pass the key flag to CPT codes saga to use bulk API if needed
        return call(fetchPACPTCodesSaga, { payload: cptPayload, key });
      });
      // Wait for ALL CPT code requests to complete before hiding loader
      yield all(cptCodeRequests);
    }

    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter on success (bulk flow only)
    }
  } catch (error: any) {
    yield put(fetchPANotApprovedReasonsFailure(error.message || 'Failed to fetch data'));
    if (key) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    // This ensures loader is turned off only after EVERYTHING completes (including all CPT codes)
    yield put(setPANotApprovedLoading(false));
  }
}

function* fetchPACPTCodesSaga(action: any): Generator<any, void, any> {
  try {
    const { payload, key } = action;

    // Use bulk API if key flag is true, otherwise use normal API
    const response: any = yield call(!key ? fetchPACPTCodes : fetchPACPTCodesBulk, payload);

    if (response && response.Data && response.Data.CptDetails) {
      const cptCodesData = response.Data.CptDetails.map((item: any) => ({
        NotApprovedCpt: item.CptCode,
        text: item.Description,
        value: item.Count,
        numeric: item.Count
      }));
      yield put(fetchPACPTCodesSuccess({
        reasonDesc: payload.ReasonDesc,
        cptCodes: cptCodesData
      }));
    } else {
      yield put(fetchPACPTCodesFailure('No CPT codes data received'));
    }
  } catch (error: any) {
    yield put(fetchPACPTCodesFailure(error.message || 'Failed to fetch CPT codes'));
  }
}

// CPT Approvals Saga
function transformCPTApprovalsData(apiResponse: any) {
  if (!apiResponse || !apiResponse.Data || !apiResponse.Data.CptDetails || apiResponse.Data.CptDetails.length === 0) {
    return [];
  }

  const cptDetails = apiResponse.Data.CptDetails;
  const transformedData: any[] = [];

  cptDetails.forEach((ele: any, index: number) => {
    if (ele.CptCode !== 'N/A' && ele.Description !== 'N/A') {
      transformedData.push({
        cptCode: ele.CptCode,
        description: ele.Description,
        transaction: ele.Count,
        count: ele.Count,
        sortData: `${index}`
      });
    }
  });

  return transformedData;
}

function* fetchCPTApprovalsSaga(action: any): Generator<any, void, any> {
  const { payload, key } = action;
  try {
    yield put(setCPTApprovalsLoading(true));

    if (key) {
      yield put(priorAuthPollingStarted()); // Increment polling counter only for bulk flow
    }

    // Use bulk API if key is truthy, otherwise use regular API
    const response: any = yield call(!key ? fetchCPTApprovalsData : fetchCPTApprovalsDataBulk, payload);

    const transformedData = transformCPTApprovalsData(response);
    const totalCount = response.Data?.CptDetails?.reduce((sum: number, item: any) => sum + item.Count, 0) || 0;

    yield put(fetchCPTApprovalsSuccess({
      data: transformedData,
      totalCount: totalCount,
      periodStart: response.Data?.PeriodStart || null,
      periodEnd: response.Data?.PeriodEnd || null,
      timeperiod: response.Data?.PeriodStart && response.Data?.PeriodEnd
        ? `${dateFormatterWithLeadingZero(response.Data.PeriodStart)} - ${dateFormatterWithLeadingZero(response.Data.PeriodEnd)}`
        : null
    }));

    if (key) {
      yield put(priorAuthPollingCompleted()); // Decrement polling counter (bulk flow only)
    }
  } catch (error: any) {
    yield put(fetchCPTApprovalsFailure(error.message || 'Failed to fetch CPT Approvals data'));
    if (key) {
      yield put(priorAuthPollingFailed(error)); // Decrement polling counter on error (bulk flow only)
    }
  } finally {
    yield put(setCPTApprovalsLoading(false));
  }
}

export function* priorAuthSaga() {
  yield takeLatest(
    PRIORAUTH_TAX_ID_QUICKFILTER_DATA_REQUEST,
    fetchPriorAuthTaxIdQuickFilterData
  );
  yield takeLatest(
    PRIORAUTH_ALL_PROVIDERS_QUICKFILTER_DATA_REQUEST,
    fetchPriorAuthAllProvidersQuickFilterData
  );
  yield takeLatest(
    PRIORAUTH_EXTENDED_TINS_TOP_FILTER_DATA_REQUEST,
    fetchPriorAuthExtendedTinsTopFilterData
  );
  yield takeLatest(
    PRIORAUTH_METRIC_DEFINITIONS_DATA_REQUEST,
    fetchPriorAuthMetricDefinitionsData
  );

  // NOTE: Individual data request watchers removed to prevent double dispatch
  // fetchAllPriorAuthData now handles all dashboard API calls when filters change
  // Individual sagas (fetchCaseSubmissionDetailsData, fetchInsightCardData, etc.)
  // are still called via the unified flow but not watched separately

  // EXCEPTION: Case Submissions Over Time needs a watcher for tab changes
  yield takeLatest(
    CASE_SUBMISSIONS_OVER_TIME_DATA_REQUEST,
    fetchCaseSubmissionsOverTimeData
  );

  // NEW: Watch for filter changes and fetch all data
  // Use debounce to prevent rapid successive calls (wait 300ms between triggers)
  yield debounce(
    300,
    PRIORAUTH_FILTERS_CHANGED,
    fetchAllPriorAuthData
  );
  // Watch PA Not Approved Reasons
  yield takeLatest(
    PRIORAUTH_NOT_APPROVED_REASONS_REQUEST,
    fetchPANotApprovedReasonsSaga
  );
  // Watch PA CPT Codes
  yield takeLatest(
    PRIORAUTH_CPT_CODES_REQUEST,
    fetchPACPTCodesSaga
  );
  // Watch CPT Approvals
  yield takeLatest(
    PRIORAUTH_CPT_APPROVALS_REQUEST,
    fetchCPTApprovalsSaga
  );
}